<template>
	<section class="bg-color-content-primary marketing-section text-color-white">
		<div class="container">
			<div class="col-10-xl mx-auto">
				<h2 v-if="featuredFundOverline" class="overline mb-100 text-color-gray-20">
					{{ featuredFundOverline }}
				</h2>
				<div class="display-flex-md justify-content-space-between">
					<article class="col-7-md mb-150-smo">
						<h3 v-if="featuredFundHeading" class="heading-1 text-color-white">
							{{ featuredFundHeading }}
						</h3>
						<RichTextComponent
							v-if="featuredFundSummary"
							:blocks="featuredFundSummary"
							:is-dark-mode="true"
						/>
						<BaseCtaLink
							v-if="featuredFundCtaLink"
							classes="mt-50"
							:link="featuredFundCtaLink"
							:is-orange="true"
							icon="arrow"
						/>
					</article>
					<div class="col-5-md text-align-center my-150-smo">
						<div v-for="(stat, i) in importantStats" :key="i" class="mb-200">
							<strong class="heading-1">{{ stat.value }}</strong>
							<p class="text-color-gray-40 mt-50">{{ stat.label }}</p>
						</div>
					</div>
				</div>

				<div class="my-400-md mb-200-smo">
					<p class="overline text-color-gray-20 mb-150"> Sectors </p>
					<VentureCapitalSectorsScroll />
				</div>

				<BasePlaceHolder v-if="showFeaturedInvestment" :line-count="4" :placehold="companyLoading">
					<div
						class="asset-holding__container module-radius-lg border-color-reversed p-300-md p-150-smo bg-color-content-primary"
					>
						<div class="asset-holding__company-container">
							<div class="asset-holding__grid-wrapper position-relative">
								<img
									:src="artificialIntelligenceCompany"
									class="asset-holding__grid-wrapper__blurred"
									alt=""
									role="presentation"
								/>
								<img
									:src="propTechCompany"
									class="asset-holding__grid-wrapper__blurred"
									alt=""
									role="presentation"
								/>
								<CompanyElementCard
									v-if="asset"
									class="asset-holding__grid-wrapper__available position-absolute"
									:asset="asset"
								/>
							</div>
						</div>
						<!-- https://fundrise.atlassian.net/browse/WWW-3824 -->
						<div v-if="showFeaturedInvestment">
							<p class="overline text-color-gray-20"> Featured investment </p>
							<h3
								:class="[
									'text-color-white font-weight-lighter line-height-125-smo mt-100',
									appStore.isMobile ? 'heading-1' : 'heading-2'
								]"
							>
								Canva
							</h3>
							<p class="mb-100 mt-50-smo">
								Canva is the leading online design and visual communication platform. Their
								collaborative and intuitive software makes it easy for anyone (even non-designers) to
								create professional designs. Founded in 2012 in Australia, Canva has seen explosive
								growth since its inception, reaching 10 million users by 2015 and 135 million users by
								2023. The company is emerging as the leading challenger of Adobe, the dominant incumbent
								player in design software.
							</p>
							<BaseCtaLink :is-orange="true" :link="canvaLink" icon="arrow" />
						</div>
					</div>
				</BasePlaceHolder>
				<p v-if="importantStatsDisclaimer" class="text-align-center mt-400-md mt-200-smo muted body-xs">{{
					importantStatsDisclaimer
				}}</p>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import { canvaCompanyId } from '@constants/reit';
import CompanyElementCard from '@components/company-element/company-element-card.vue';
import { getAsset } from '@api/sanity/proxy/assets';
import { getVentureMarketingPageFeaturedFund } from '@api/sanity/graphql/venture-marketing-page';
import { Link } from 'types/layout';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityMarketingAboutFund } from 'types/sanity/marketing-components';
import { SanityRichTextBlock } from 'types/sanity';
import { useAppStore } from '@stores/app';
import VentureCapitalSectorsScroll from '@components/marketing-site/venture-capital/venture-capital-sectors-scroll.vue';
import { VentureImportantStat } from 'types/sanity/venture-marketing';

const appStore = useAppStore();

const showFeaturedInvestment = false;
const canvaLink: Link = {
	router: 'blog-post',
	params: { blogPostUrlSlug: 'investment-in-canva' },
	target: '_blank',
	text: 'Read more about this investment'
};
const companyLoading = ref(true);
const artificialIntelligenceCompany = `${appCloudfrontPath}/images/offerings/growth-tech/gto-ai-holding.png`;

const propTechCompany = `${appCloudfrontPath}/images/offerings/growth-tech/gto-prop-tech-holding.png`;

const asset = ref<Asset | null>(null);

const sanityFeaturedFundData: Ref<SanityMarketingAboutFund | null> = ref(null);

const featuredFundOverline = computed((): string => sanityFeaturedFundData.value?.featuredOverline ?? '');
const featuredFundHeading = computed((): string => sanityFeaturedFundData.value?.featuredHeading ?? '');
const featuredFundSummary: Ref<Array<SanityRichTextBlock>> = ref([]);
const featuredFundCtaLink: Ref<Link | null> = ref(null);

const importantStats = computed(
	(): Array<VentureImportantStat> => sanityFeaturedFundData.value?.featuredPanelStats ?? []
);
const importantStatsDisclaimer = computed((): string => sanityFeaturedFundData.value?.featuredDisclaimer ?? '');

async function setup(): Promise<void> {
	try {
		asset.value = await getAsset(canvaCompanyId, { elementType: 'VENTURE' });
		const sanityResponse = await getVentureMarketingPageFeaturedFund();

		sanityFeaturedFundData.value = sanityResponse;

		featuredFundSummary.value = sanityResponse?.featuredSummaryRaw ?? [];
		featuredFundCtaLink.value = sanityResponse?.featuredFundCta
			? {
					href: sanityResponse?.featuredFundCta?.url ?? '',
					text: sanityResponse?.featuredFundCta?.linkText ?? '',
					target: sanityResponse?.featuredFundCta?.openInNewTab ? '_blank' : '_self'
				}
			: null;

		companyLoading.value = false;
	} catch {}
}
setup();
</script>
<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;

// Supports sector cards in horizontal scroll extending to viewport edge.
:deep(.horizontal) {
	@include respond-to(lg) {
		width: calc((100% + (100vw - 100%) / 2) - $md-viewport-padding);
		max-width: revert;
		margin-right: calc(50% - 50vw);
	}
}

$small-screen-size-break: 568px;
$medium-screen-size-break: 981px;
.asset-holding {
	&__container {
		display: flex;
		flex-direction: column-reverse;
		@media screen and (min-width: $medium-screen-size-break) {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			column-gap: 4.5rem;
		}
		@media screen and (max-width: $small-screen-size-break - 1) {
			padding-bottom: 6.5rem;
		}
		@media screen and (min-width: $small-screen-size-break - 1) and (max-width: $medium-screen-size-break) {
			padding-bottom: 4.5rem;
		}
	}
	&__company-container {
		display: flex;
		grid-row: 1;
		justify-content: center;
		@media screen and (min-width: $medium-screen-size-break) {
			align-self: center;
			grid-row: unset;
			justify-content: unset;
		}
		@media screen and (max-width: $small-screen-size-break - 1) {
			margin-top: 7rem;
		}
		@media screen and (min-width: $small-screen-size-break) and (max-width: $medium-screen-size-break) {
			margin-top: 5rem;
		}
	}
	&__grid-wrapper {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		&__blurred {
			filter: blur(3px);
			-webkit-filter: blur(3px);
			opacity: 0.1;
			max-width: none;
			width: 160px;
			height: 170px;
			@media screen and (min-width: $small-screen-size-break) {
				width: 260px;
				height: 232px;
			}
		}
		&__available {
			height: 310px;
			width: 271px;
			top: -40%;
			left: 7.5%;
			@media screen and (min-width: $small-screen-size-break) {
				height: 308px;
				width: 336px;
				left: 18%;
				top: -16%;
			}
		}
	}
}
</style>
